/**
 * FocalPoint
 * The container that will maintain focus
 */

import { useFloatingTree } from '@floating-ui/react';
import { getFloatingChildren } from 'components/popovers/utils';
import React, { useCallback, useEffect, useRef } from 'react';

let timer: number | null = null;
const isDOM = typeof document !== 'undefined';

interface Props {
  element?: string;
  className?: string;
  children?: React.ReactNode;

  /**
   * Because we potentially need to look across two portals here (`react-portal` for `SuperModal`
   * and `@floating-ui`'s own portal for `Popover2`) we need to know the ID of the containing
   * floating node so we don't clear focus away from the popover's portal when it's rendered inside
   * a modal.
   */
  floatingNodeId: string;
}

const FocalPoint: React.FC<Props> = ({ element = 'div', className, children, floatingNodeId }) => {
  const anchor = useRef(isDOM ? document.activeElement : null);
  const rootRef = useRef<HTMLElement>();

  const tree = useFloatingTree();

  const focus = useCallback(() => {
    const popoverContainsFocusTarget =
      tree &&
      getFloatingChildren(tree.nodesRef.current, floatingNodeId).some(
        node =>
          node.context?.refs.floating.current?.contains(document.activeElement) ||
          node.context?.refs.domReference.current?.contains(document.activeElement)
      );

    if (!rootRef.current?.contains(document.activeElement) && !popoverContainsFocusTarget) {
      rootRef.current?.focus();
    }
  }, [floatingNodeId, tree]);

  const trapFocus = useCallback(() => {
    window.clearTimeout(timer!);
    timer = window.setTimeout(focus, 10);
  }, [focus]);

  const returnFocus = useCallback(() => {
    // When transitioning between pages using hash route state,
    // this anchor is some times lost. Do not attempt to focus
    // on a non-existent anchor.
    if (anchor.current instanceof HTMLElement && typeof anchor.current.focus === 'function') {
      anchor.current.focus();
    }
  }, []);

  const handleBlur = useCallback(
    (event: FocusEvent) => {
      if (
        anchor.current &&
        event.target instanceof Node &&
        anchor.current.contains(event.target) === false
      ) {
        event.preventDefault();
        trapFocus();
      }
    },
    [trapFocus]
  );

  useEffect(() => {
    trapFocus();
    document.addEventListener('focus', handleBlur, true);

    return () => {
      document.removeEventListener('focus', handleBlur, true);

      clearTimeout(timer!);

      returnFocus();
    };
  }, [handleBlur, returnFocus, trapFocus]);

  return React.createElement(
    element ?? 'div',
    {
      ref: rootRef,
      tabIndex: 0,
      className,
    },
    children
  );
};

export default FocalPoint;
